import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogTitle, IconButton } from "@mui/material";
import { useRef } from "react";

import type { AgentThreadToolUseContent } from "@/shared/domain/agentThread/AgentThreadRecord";
import type { ClientTool } from "@/shared/domain/agentThread/clientTools";
import { confirmationContentFor } from "@/shared/domain/agentThread/clientTools";
import { Button } from "@/shared/foundation/primitives";

import styles from "./ToolApprovalDetailsModal.module.css";

interface ToolApprovalDetailsModalProps {
  open: boolean;
  onClose: () => void;
  toolUse: AgentThreadToolUseContent;
  tool: ClientTool;
  /** Whether this tool_use has already been approved or declined. */
  resolved: boolean;
  onApprove: () => void;
  onDecline: () => void;
}

/**
 * Detail modal for previewing what a client-side tool will do.
 *
 * Opened from the inline ToolApprovalCard via the "Details" button.
 * Shows the tool's ConfirmationContent (or a JSON fallback) along with
 * Approve/Decline actions so the user can act without closing the modal.
 *
 * Approve/Decline are deferred until after the Dialog exit animation
 * completes to avoid unmounting the parent ToolApprovalCard (and this
 * Dialog) mid-transition.
 *
 * Once `resolved`, both actions are disabled rather than left clickable: the
 * card's handlers already return early for a second decision, so an enabled
 * Approve here offered the user an action that did nothing.
 */
export function ToolApprovalDetailsModal({
  open,
  onClose,
  toolUse,
  tool,
  resolved,
  onApprove,
  onDecline,
}: ToolApprovalDetailsModalProps) {
  const params = toolUse.input ?? {};
  // Falls back to the raw JSON when the tool would reject these params: a
  // preview typed to the tool's validated input must not be handed a proposal
  // that isn't one, and raw JSON is the honest view of a malformed request.
  const CustomContent = confirmationContentFor(tool, params);

  // Store the action to execute after the Dialog exit animation finishes.
  const pendingActionRef = useRef<(() => void) | null>(null);

  const handleApprove = () => {
    pendingActionRef.current = onApprove;
    onClose();
  };

  const handleDecline = () => {
    pendingActionRef.current = onDecline;
    onClose();
  };

  const handleExited = () => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    action?.();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: handleExited }}
    >
      <DialogTitle className={styles.dialogTitle}>
        <span className={styles.titleText}>{tool.displayName}</span>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <div className={styles.content}>
        {CustomContent ? (
          <CustomContent params={params} />
        ) : (
          <pre className={styles.jsonPreview}>
            {JSON.stringify(params, null, 2)}
          </pre>
        )}
      </div>

      <div className={styles.actions}>
        <Button
          variant="outlined"
          onClick={handleDecline}
          size="small"
          disabled={resolved}
        >
          Decline
        </Button>
        <Button
          variant="contained"
          onClick={handleApprove}
          size="small"
          disabled={resolved}
        >
          Approve
        </Button>
      </div>
    </Dialog>
  );
}
